import { Injectable } from '@angular/core';
import { map, Observable, Subject, tap } from 'rxjs';
import { Notification } from '../components/notifications/notifications';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { DEFAULT_TOAST_TIMEOUT, activeTranslations } from '../enum/constants';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private _subject = new Subject<Notification>();
  private _idx = 0;
  public notificationsCount: number = 0;

  public unreadNotificationList: any[] = [];
  public readNotificationList: any[] = [];

  constructor(
    private toastr: ToastrService,
    private http: HttpClient,
  ) {
  }

  getObservable(): Observable<Notification> {
    return this._subject.asObservable();
  }

  private notificationShown: boolean = false;

  private showToast(
    type: 'info' | 'success' | 'warning' | 'error',
    title: string,
    message: string,
    timeout?: number,
    stack: boolean = false,
  ) {
    if (message == null) return;
    if (!stack && this.notificationShown) return;

    if (!stack) this.notificationShown = true;

    const toastrRef = this.toastr[type](message, title, {
      disableTimeOut: true,
      enableHtml: true,
      toastClass: `notification ${type}`,
      titleClass: 'custom-title-class',
      messageClass: 'custom-message-class',
      closeButton: true,
      tapToDismiss: false,
    });

    let timeoutId!: NodeJS.Timeout;
    toastrRef.onShown.subscribe(() => {
      timeoutId = setTimeout(() => {
        this.toastr.remove(toastrRef.toastId);
        if (!stack) this.notificationShown = false;
      }, this.getToastTimeout(message, timeout));
    });

    toastrRef.onHidden.subscribe(() => {
      clearTimeout(timeoutId);
      if (!stack) this.notificationShown = false;
    });
  }

  showInfo(title: string, message: string, timeout?: number, stack: boolean = false) {
    this.showToast('info', title, message, timeout, stack);
  }

  showInfoV2(title: string, message: string, timeout?: number): ActiveToast<any> {
    return this.toastr.info(message, title, {
      enableHtml: true,
      toastClass: 'notification info',
      titleClass: 'custom-title-class',
      messageClass: 'custom-message-class',
      closeButton: true,
      tapToDismiss: true,
      timeOut: this.getToastTimeout(message, timeout),
    });
  }

  showSuccess(title: string, message: string, timeout?: number, stack: boolean = false) {
    this.showToast('success', title, message, timeout, stack);
  }

  showWarning(title: string, message: string, timeout?: number, stack: boolean = false) {
    this.showToast('warning', title, message, timeout, stack);
  }

  showError(title: string = activeTranslations.notifications_texts.error.error, message: string, timeout?: number, stack: boolean = false) {
    this.showToast('error', title, message, timeout, stack);
  }

  // debug(title: string, message: string, timeout = 0) {
  //   if ((message != null) && (title != null)) {
  //     this._subject.next(new Notification(this._idx++, NotificationType.debug, title, message, timeout));
  //   }
  // }

  playSound() {
    // const audio: HTMLAudioElement = new Audio('/assets/mp3/toast_sound.mp3');
    const audio = document.getElementById('audio-toast') as HTMLAudioElement;
    if (!audio) console.log('audio not found!');
    audio.autoplay = true;
    audio.muted = false;
    audio.play();
  }

  getLastNotification() {
    const url: string = environment.baseURL + '/notification/last';
    return this.http.get<any>(url).pipe(map(v => v.data), tap(v => this.readNotificationList = v));
  }

  getUnreadNotification() {
    const url: string = environment.baseURL + '/notification/last/unread';
    return this.http.get<any>(url).pipe(map(v => v.data), tap(v => this.unreadNotificationList = v));
  }

  getAllNotification(params: any) {
    const url: string = `${environment.baseURL}/notification/all`;
    return this.http.get<any>(url, { params }).pipe(map(v => v.data));
  }

  getCountNotification() {
    const url: string = environment.baseURL + '/notification/count';
    return this.http.get<any>(url).pipe(map(v => v.data), tap(v => this.notificationsCount = v));
  }

  getAllNotifications(): Observable<any[]> {
    const url = environment.baseURL + '/notification';
    return this.http.get<any>(url).pipe(map(v => v.data));
  }

  readAllNotification() {
    const url: string = environment.baseURL + '/notification/read-all';
    return this.http.post<any>(url, {}).pipe(map(v => v.data));
  }

  readNotification(id: string) {
    const url: string = environment.baseURL + `/notification/read/${id}`;
    return this.http.post<any>(url, {}).pipe(map(v => v.data));
  }

  getToastTimeout(text: string, timeout?: number): number {
    if (timeout) return timeout;

    const length = text.split(' ').length;
    const time = Math.round((length / 3 + 1) * 1000);

    if (time < DEFAULT_TOAST_TIMEOUT) return DEFAULT_TOAST_TIMEOUT;
    return time;
  }
}
